import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { User as UserIcon, PlusCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getMyPrayers, fetchLiveCounts } from '../lib/api';
import { sortLiveFirst, editedTime } from '../lib/feedSort';
import PrayerCard from '../components/PrayerCard';

// Small inline praying-hands icon (matches lucide stroke style)
const PrayingHands = ({ size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11 3 C 9.5 5, 7.5 9, 7 14 C 6.7 17, 8 19, 9.5 20 L 11.5 20 Z" />
    <path d="M13 3 C 14.5 5, 16.5 9, 17 14 C 17.3 17, 16 19, 14.5 20 L 12.5 20 Z" />
    <line x1="12" y1="3.5" x2="12" y2="20" />
  </svg>
);

/**
 * "My Prayers" page — routed at /feed/clips. Lists every prayer authored
 * by the current user, plus quick actions to add a new one or manage
 * existing ones.
 *
 * Layout intentionally mirrors "My Lit Candles" (LitCandles.jsx) so the
 * two sidebar pages read as siblings: same header block (icon badge +
 * title + subtitle), same content padding, same responsive PrayerCard
 * grid.
 */
const EmptyState = ({ icon: Icon, title, subtitle, cta, onCta }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center px-6" data-testid="your-prayers-empty">
    <div className="w-16 h-16 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
      <Icon size={28} className="text-amber-500" />
    </div>
    <h3 className="text-lg font-medium mb-1">{title}</h3>
    <p className="text-sm text-zinc-500 max-w-md">{subtitle}</p>
    {cta && (
      <button
        onClick={onCta}
        data-testid="your-prayers-empty-cta"
        className="mt-5 h-9 px-4 rounded-full text-sm font-medium bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors"
      >
        {cta}
      </button>
    )}
  </div>
);

const YourPrayers = () => {
  const { user, loading: authLoading, openAuthModal: login } = useAuth();
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Fresh live-praying counts per prayer id — polled every 5s so the
  // LIVE badge tracks the same 15s participant window that the watch
  // page uses. Feeds into sortLiveFirst() below so any prayer with
  // someone currently praying it floats to the top.
  const [liveCountsById, setLiveCountsById] = useState({});

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMyPrayers()
      .then((data) => { if (!cancelled) setPrayers(Array.isArray(data) ? data : []); })
      .catch((e) => {
        console.error('my prayers fetch failed', e);
        if (!cancelled) setError('Could not load your prayers.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  // Live-counts poll — mirrors the pattern used on Home for the same
  // 5-second cadence and 15-second participant window. Empty list =
  // no-op so we don't hammer the endpoint when the user has no
  // prayers yet.
  useEffect(() => {
    if (!prayers.length) { setLiveCountsById({}); return undefined; }
    let cancelled = false;
    const ids = prayers.map((p) => p.id);
    const tick = async () => {
      try {
        const counts = await fetchLiveCounts(ids);
        if (!cancelled) setLiveCountsById(counts);
      } catch { /* silent — sort just uses last known counts */ }
    };
    tick();
    const interval = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [prayers.map((p) => p.id).join('|')]);

  // Sort: prayers currently being prayed first, then by last-edited
  // (falls back to created_at for prayers never edited).
  const displayedPrayers = useMemo(
    () => sortLiveFirst(prayers, liveCountsById, editedTime),
    [prayers, liveCountsById],
  );

  if (authLoading) {
    return <div className="px-6 py-20 text-center text-zinc-500">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="px-4 sm:px-6 py-20" data-testid="your-prayers-signed-out">
        <EmptyState
          icon={UserIcon}
          title="Sign in to see your prayers"
          subtitle="Your shared prayers live here — signed in."
          cta="Sign in"
          onCta={login}
        />
      </div>
    );
  }

  return (
    <div data-testid="your-prayers-page">
      {/* Header — mirrors My Lit Candles so the two sibling pages share a
          unified visual language. Icon badge on the left, title +
          subtitle in the middle, primary actions on their own row
          underneath so the title / description reads cleanly first. */}
      <div className="px-4 sm:px-6 pt-6 pb-4 border-b border-zinc-200 dark:border-zinc-800">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-full overflow-hidden bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center text-amber-600 font-semibold text-lg"
            data-testid="your-prayers-avatar"
          >
            {user.picture ? (
              <img src={user.picture} alt={user.name || 'Profile'} className="w-full h-full object-cover" />
            ) : (
              (user.name || user.handle || '?').charAt(0).toUpperCase()
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold" data-testid="your-prayers-title">My Prayers</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
              Every prayer you&apos;ve shared, most recent first.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to="/add-prayer"
            data-testid="add-prayer-btn"
            className="h-9 px-4 rounded-full text-sm font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors flex items-center gap-1.5"
          >
            <PlusCircle size={16} /> Add prayer
          </Link>
          <Link
            to="/manage-prayers"
            data-testid="manage-btn"
            className="h-9 px-4 rounded-full text-sm font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors flex items-center gap-1.5"
          >
            <UserIcon size={16} /> Manage prayers
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 py-6">
        {loading && (
          <div
            className="py-16 text-center text-zinc-500 flex items-center justify-center gap-2"
            data-testid="your-prayers-loading"
          >
            <Loader2 className="animate-spin" size={16} /> Loading your prayers…
          </div>
        )}
        {!loading && error && (
          <div className="py-16 text-center text-red-600 dark:text-red-400 text-sm" data-testid="your-prayers-error">
            {error}
          </div>
        )}
        {!loading && !error && prayers.length === 0 && (
          <EmptyState
            icon={PrayingHands}
            title="No prayers yet"
            subtitle="Share your first prayer with the community — everything you post will show up here."
            cta="Add prayer"
            onCta={() => window.location.assign('/add-prayer')}
          />
        )}
        {!loading && !error && prayers.length > 0 && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-x-4 gap-y-8"
            data-testid="your-prayers-grid"
          >
            {displayedPrayers.map((p) => {
              const merged = liveCountsById[p.id] !== undefined
                ? { ...p, online_praying_now_count: liveCountsById[p.id] }
                : p;
              return <PrayerCard key={p.id} prayer={merged} />;
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default YourPrayers;
